import { api } from '../client'

export interface UserPreferences {
  darkMode: boolean
  locale: string
  sidebarCollapsed: boolean
  preferredViewType: '2d' | '3d'
  // Mirrors ControllerMapping (incl. the Phase 5 RobotControlMapping `robot`
  // sub-object and the PartialCctvControlMapping `cctv` sub-object) from
  // '@/lib/controller/gamepad-core' — duplicated inline because this
  // API-domain layer only imports from '../client'. Like the three original
  // keys, the `robot` mirror describes the canonical FULL shape; `cctv`
  // mirrors the stored possibly-partial shape (ControllerMapping declares the
  // partial type). Stored partial saves are deep-merged at runtime via
  // `resolveRobotMapping`/`resolveCctvMapping`.
  controllerMapping?: {
    deadmanButton: number
    invertTilt: boolean
    swapSticks: boolean
    robot?: {
      axes: {
        vx: { axis: number; invert: boolean }
        vy: { axis: number; invert: boolean }
        yaw: { axis: number; invert: boolean }
      }
      buttons: { stand?: number; lie?: number; handshake?: number; twoLeg?: number; estop?: number }
    }
    cctv?: {
      axes?: {
        pan?: { axis?: number; invert?: boolean }
        tilt?: { axis?: number; invert?: boolean }
      }
    }
  }
}

export interface User {
  id: string
  email: string
  name: string
  role: 'admin' | 'publisher' | 'viewer'
  status: 'active' | 'suspended' | 'deleted'
  tenantId: string
  tenantName?: string
  assignedModules?: string[]
  tenantUserLimits?: { admin: number; publisher: number; viewer: number } | null
  licenseExpiresAt?: string | null
  mfaEnabled?: boolean
  isGuest?: boolean
  /**
   * Plan 6a Task 8 — central Organization's deployment mode, sourced from
   * `Organization.deploymentMode` and surfaced on `/users/me` for both
   * central and tenant callers. Retained for consumers/telemetry; no longer
   * drives any UI (the "On-Prem" header badge was removed in #706). Falls back
   * to `'cloud'` when missing.
   */
  deploymentMode?: 'cloud' | 'onprem'
  /**
   * Task 3 — effective sitewide-assistant availability: the AND of the
   * operator's per-tenant flag (`Tenant.assistantEnabledByOperator`) and the
   * tenant admin's own opt-out (`Organization.settings.assistant.enabledByTenant`).
   * Absent means enabled — defaults defensively to `true` (see `useAuth.tsx`
   * `fetchUserProfile` + the localStorage rehydration path), same as
   * `deploymentMode`.
   */
  assistantEnabled?: boolean
  createdAt: string
  updatedAt: string
  preferences?: Partial<UserPreferences>
}

export const authApi = {
  getMe: () => api.get<User>('/users/me'),

  updatePreferences: (preferences: Partial<UserPreferences>) => api.put<User>('/users/me/preferences', preferences),
}
